import { CONTENT, LAYOUT, VISIBILITY } from './registry';
import { cellSettings } from './columns/CellRegistry';
import {
  entryKindSettings,
  entryUsesCell,
  entryUsesLabel,
} from './detail/EntryRegistry';
import {
  filterSettings,
  filterIsRange,
  filterUsesLookup,
} from './filters/FilterRegistry';

export { CONTENT, LAYOUT, VISIBILITY };

/**
 * The vocabulary a configuration option is declared in, and the options every surface offers.
 *
 * A cell, an entry kind and a filter type each declare what they read of the entry drawing them, so
 * the editor offers exactly that rather than a fixed row of controls that grows whenever a type
 * does. A declaration says:
 *
 *   name      - the field it writes on the entry
 *   label     - what it is called, as a message key
 *   control   - text, number, switch, choice, icon, colour or key
 *   category  - CONTENT for what is shown, LAYOUT for how it is shown
 *   choices   - what a choice offers, as `{ value, label }`
 *   when      - whether the entry is one this applies to; offered always when absent
 *   help      - a message key shown under the control
 *
 * The rest - a placeholder, the bounds of a number - is passed to the control as declared.
 */

/** How large an entry's value is drawn. */
export const SCALES = [
  { value: '', label: 'SCALE_MEDIUM' },
  { value: 'small', label: 'SCALE_SMALL' },
  { value: 'large', label: 'SCALE_LARGE' },
  { value: 'heading', label: 'SCALE_HEADING' },
];

/** Where an entry sits in the room it was given. */
export const ALIGNMENTS = [
  { value: '', label: 'ALIGN_START' },
  { value: 'center', label: 'ALIGN_CENTER' },
  { value: 'end', label: 'ALIGN_END' },
];

/** How an entry's caption is cased, whatever the wording behind it says. */
export const CAPTION_CASES = [
  { value: '', label: 'CASE_DEFAULT' },
  { value: 'uppercase', label: 'CASE_UPPER' },
  { value: 'capitalize', label: 'CASE_CAPITALIZED' },
  { value: 'lowercase', label: 'CASE_LOWER' },
  { value: 'none', label: 'CASE_AS_WRITTEN' },
];

/** Where an entry's caption is drawn. */
export const CAPTION_PLACEMENTS = [
  { value: '', label: 'CAPTION_BESIDE' },
  { value: 'column', label: 'CAPTION_ABOVE' },
];

/** Where a column is shown on a card, for the surfaces that have one. */
export const SLOTS = [
  { value: '', label: 'SLOT_AUTO' },
  { value: 'primary', label: 'SLOT_PRIMARY' },
  { value: 'secondary', label: 'SLOT_SECONDARY' },
  { value: 'trailing', label: 'SLOT_TRAILING' },
  { value: 'hidden', label: 'SLOT_HIDDEN' },
];

/** The operators the query builder accepts for a single value; the blank one means `like`. */
export const OPERATORS = [
  { value: '', label: 'CONTAINS' },
  { value: 'eq', label: 'EQUALS' },
  { value: 'gt', label: 'GREATER_THAN' },
  { value: 'lt', label: 'LESS_THAN' },
];

/**
 * What every entry of a detail region carries, whatever draws it.
 *
 * A field, a heading and a rule are all placed in the same grid, so how much room one takes and
 * where it sits in it are the region's arrangement rather than the cell's business.
 */
export const ENTRY_SETTINGS = [
  // What is drawn of a value, offered only where a value is drawn: a rule has nothing to capitalise.
  {
    name: 'label',
    label: 'LABEL',
    control: 'text',
    category: CONTENT,
    when: (entry) => entryUsesCell(entry?.kind) || entryUsesLabel(entry?.kind),
  },
  {
    name: 'noCaption',
    label: 'HIDE_CAPTION',
    control: 'switch',
    category: CONTENT,
    when: (entry) => entryUsesCell(entry?.kind),
  },
  {
    name: 'colon',
    label: 'CAPTION_COLON',
    control: 'switch',
    category: CONTENT,
    when: (entry) => entryUsesCell(entry?.kind),
  },
  {
    name: 'capitalize',
    label: 'CAPITALIZE',
    control: 'switch',
    category: CONTENT,
    when: (entry) => entryUsesCell(entry?.kind),
  },
  {
    name: 'hideWhenEmpty',
    label: 'HIDE_WHEN_EMPTY',
    control: 'switch',
    category: CONTENT,
    when: (entry) => entryUsesCell(entry?.kind),
  },
  {
    name: 'scale',
    label: 'SCALE',
    control: 'choice',
    choices: SCALES,
    category: LAYOUT,
    when: (entry) => entryUsesCell(entry?.kind),
  },
  {
    name: 'direction',
    label: 'CAPTION_PLACEMENT',
    control: 'choice',
    choices: CAPTION_PLACEMENTS,
    category: LAYOUT,
    when: (entry) => entryUsesCell(entry?.kind),
  },
  {
    name: 'captionCase',
    label: 'CAPTION_CASE',
    control: 'choice',
    choices: CAPTION_CASES,
    category: LAYOUT,
    when: (entry) => entryUsesCell(entry?.kind),
  },
  {
    name: 'icon',
    label: 'ICON',
    control: 'icon',
    category: LAYOUT,
    when: (entry) => entryUsesCell(entry?.kind),
  },
  {
    name: 'emphasis',
    label: 'EMPHASISE',
    control: 'switch',
    category: LAYOUT,
    when: (entry) => entryUsesCell(entry?.kind),
  },
  // Where it sits in the grid, which every entry of a region has whatever it draws.
  {
    name: 'align',
    label: 'ALIGNMENT',
    control: 'choice',
    choices: ALIGNMENTS,
    category: LAYOUT,
  },
  {
    name: 'newRow',
    label: 'START_NEW_ROW',
    control: 'switch',
    category: LAYOUT,
  },
  {
    name: 'padding',
    label: 'PADDING',
    control: 'text',
    placeholder: '8px 16px',
    category: LAYOUT,
  },
];

/** What every column of a list carries. A column is drawn in a row, and on a card of its own. */
export const COLUMN_SETTINGS = [
  { name: 'label', label: 'LABEL', control: 'text', category: CONTENT },
  {
    name: 'noCaption',
    label: 'HIDE_CAPTION',
    control: 'switch',
    category: CONTENT,
  },
  {
    name: 'capitalize',
    label: 'CAPITALIZE',
    control: 'switch',
    category: CONTENT,
  },
  {
    name: 'slot',
    label: 'CARD_SLOT',
    control: 'choice',
    choices: SLOTS,
    category: LAYOUT,
  },
];

/** What every filter carries, whichever field collects it. */
export const FILTER_SETTINGS = [
  { name: 'label', label: 'LABEL', control: 'text', category: CONTENT },
  // A span is applied as two terms rather than one, so its operators are the compiler's business.
  // What is left to say is whether the ends themselves match.
  {
    name: 'operator',
    label: 'OPERATOR',
    control: 'choice',
    choices: OPERATORS,
    category: CONTENT,
    when: (entry) => !filterIsRange(entry?.type),
  },
  {
    name: 'includeExtremes',
    label: 'INCLUDE_EXTREMES',
    control: 'switch',
    category: CONTENT,
    when: (entry) => filterIsRange(entry?.type),
  },
  {
    name: 'defaultValue',
    label: 'DEFAULT_VALUE',
    control: 'text',
    help: 'VALUES_ARE_TEXT',
    category: CONTENT,
  },
  {
    name: 'multiple',
    label: 'MULTIPLE',
    control: 'switch',
    category: CONTENT,
  },
  // A list asked for once, or asked again as text is typed: the size of the list decides, so it is
  // the layout's to say rather than the type's.
  {
    name: 'searchable',
    label: 'SEARCHABLE',
    control: 'switch',
    category: CONTENT,
    when: (entry) => filterUsesLookup(entry?.type),
  },
  // Only a field asking for a list has anything to ask it with, so only one can be told to wait for
  // a sibling before asking.
  {
    name: 'dependsOn',
    label: 'DEPENDS_ON',
    control: 'dependsOn',
    category: VISIBILITY,
    when: (entry) => filterUsesLookup(entry?.type),
  },
];

/**
 * What every entry carries whatever surface it is on: when it is shown at all.
 *
 * Offered last, so a surface or a type saying more about it has the later word.
 */
export const BINDING_SETTINGS = [
  {
    name: 'visibleWhen',
    label: 'VISIBLE_WHEN',
    control: 'predicate',
    category: VISIBILITY,
  },
];

/** What a surface gives every one of its entries. */
const SETTINGS_BY_SURFACE = {
  detail: ENTRY_SETTINGS,
  list: COLUMN_SETTINGS,
  filters: FILTER_SETTINGS,
};

/** What the type of an entry on this surface declares of its own. */
const typeSettings = (entry, surface) => {
  if (surface === 'filters') return filterSettings(entry?.type);

  // A kind and a cell both have a say: the kind decides what the entry is, the cell how its value
  // reads, and an entry drawn by a cell has both.
  return [...entryKindSettings(entry?.kind), ...cellSettings(entry?.cell)];
};

/**
 * The settings offered for an entry: the ones its surface gives every entry, then its type's own.
 *
 * A setting the entry is not one for - an operator on a span, a search flag on a fixed list - is
 * left out rather than shown inert.
 *
 * @param {Object} entry - The layout entry being configured.
 * @param {String} surface - The surface it belongs to.
 *
 * @returns {Array}
 */
export const settingsFor = (entry, surface) =>
  [
    ...(SETTINGS_BY_SURFACE[surface] ?? []),
    ...typeSettings(entry, surface),
    ...BINDING_SETTINGS,
  ]
    .filter(({ when }) => !when || when(entry))
    // A type may say more about a setting its surface already offers, and its word is the later one.
    .filter(
      (setting, at, held) =>
        held.findLastIndex(({ name }) => name === setting.name) === at
    );

/**
 * Those of a set of settings that decide one thing - what is shown, or how.
 *
 * @param {Array} settings - The settings offered.
 * @param {String} category - CONTENT or LAYOUT.
 *
 * @returns {Array}
 */
export const settingsIn = (settings, category) =>
  settings.filter((setting) => setting.category === category);
